<template>
  <v-dialog v-bind:value="value"
            v-on:input="$emit('input', $event)"
            width="500">
    <v-card>
      <v-card-title>
        <span class="headline">{{ $t('addPriceTag') }}</span>
      </v-card-title>
      <v-form ref="form"
              v-on:submit.prevent="confirm">
        <v-card-text>
          <v-text-field class="mb-6"
                        autofocus
                        filled
                        hide-details="auto"
                        v-bind:label="$t('title')"
                        v-bind:rules="[ rules.required ]"
                        v-model.trim="product.publicTitle" />
          <v-text-field class="mb-6"
                        filled
                        hide-details="auto"
                        suffix="€"
                        v-bind:label="$t('price')"
                        v-bind:rules="[ rules.required, rules.decimal ]"
                        v-model.trim="product.price" />
          <v-text-field class="mb-6"
                        filled
                        hide-details="auto"
                        type="number"
                        min="1"
                        step="1"
                        v-bind:label="$t('count')"
                        v-bind:rules="[ rules.required, rules.positiveInteger ]"
                        v-model.number="product.count" />
          <v-text-field class="mb-6"
                        filled
                        hide-details="auto"
                        v-bind:label="$t('numberOptional')"
                        v-model.trim="product.productId" />
          <v-select class="mb-6"
                    filled
                    hide-details="auto"
                    v-bind:items="labelItems"
                    v-bind:label="$t('label')"
                    v-model="product.label" />
          <v-text-field class="mb-6"
                        filled
                        hide-details="auto"
                        type="number"
                        min="1"
                        step="1"
                        v-bind:label="$t('bottlesPerBundleOptional')"
                        v-bind:rules="[ rules.optionalPositiveInteger ]"
                        v-model.number="product.quantity" />
          <v-text-field filled
                        hide-details="auto"
                        suffix="€"
                        v-bind:label="$t('depositPerBottle')"
                        v-bind:rules="[ rules.decimal ]"
                        v-model.trim="product.depositBaseUnit" />
        </v-card-text>
      </v-form>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn color="secondary"
               text
               v-on:click="cancel">
          {{ $t('cancel') }}
        </v-btn>
        <v-btn color="accent"
               text
               v-on:click="confirm">
          {{ $t('add') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  const DECIMAL_PATTERN = /^\d+([.,]\d{1,2})?$/

  const productDefaults = () => ({
    publicTitle: '',
    price: '',
    count: 1,
    productId: '',
    label: '',
    quantity: null,
    depositBaseUnit: '0,00'
  })

  let keyCounter = 0

  export default {
    computed: {
      labelItems() {
        return [
          { text: this.$t('labelNone'), value: '' },
          { text: this.$t('labelMehrweg'), value: 'MEHRWEG' },
          { text: this.$t('labelEinweg'), value: 'EINWEG' }
        ]
      },
      rules() {
        return {
          required: v => (v != null && `${v}`.trim().length > 0) || this.$t('global.pleaseFillOutThisField'),
          decimal: v => (v == null || `${v}`.trim().length === 0 || DECIMAL_PATTERN.test(`${v}`.trim())) || this.$t('invalidAmount'),
          positiveInteger: v => (Number.isInteger(v) && v > 0) || this.$t('invalidCount'),
          optionalPositiveInteger: v => (v == null || v === '' || (Number.isInteger(v) && v > 0)) || this.$t('invalidCount')
        }
      }
    },
    data: () => ({
      product: productDefaults()
    }),
    methods: {
      parseDecimal(value) {
        if (value == null || `${value}`.trim().length === 0) {
          return 0
        }

        const number = Number(`${value}`.trim().replace(',', '.'))

        return isNaN(number) ? 0 : number
      },
      reset() {
        this.product = productDefaults()
        this.$refs.form?.resetValidation()
      },
      cancel() {
        this.reset()
        this.$emit('input', false)
      },
      confirm() {
        if (!this.$refs.form.validate()) {
          return
        }

        const p = this.product

        keyCounter += 1

        this.$emit('add', {
          key: `manual-${Date.now()}-${keyCounter}`,
          manual: true,
          publicTitle: p.publicTitle,
          price: this.parseDecimal(p.price),
          count: p.count,
          productId: p.productId || '',
          label: p.label || '',
          quantity: p.quantity > 0 ? p.quantity : null,
          depositBaseUnit: this.parseDecimal(p.depositBaseUnit)
        })

        this.reset()
        this.$emit('input', false)
      }
    },
    name: 'ManualPriceTagDialog',
    props: {
      value: Boolean
    },
    watch: {
      value(open) {
        if (open) {
          this.reset()
        }
      }
    }
  }
</script>

<i18n>
  {
    "de": {
      "addPriceTag": "Preisschild hinzufügen",
      "title": "Artikelbezeichnung",
      "price": "Preis",
      "count": "Anzahl Schilder",
      "numberOptional": "Artikelnummer (optional)",
      "label": "Mehrweg / Einweg",
      "labelNone": "Leer",
      "labelMehrweg": "Mehrweg",
      "labelEinweg": "Einweg",
      "bottlesPerBundleOptional": "Flaschen pro Gebinde (optional)",
      "depositPerBottle": "Pfand je Flasche",
      "add": "Hinzufügen",
      "cancel": "Abbrechen",
      "invalidAmount": "Bitte einen gültigen Betrag eingeben (z. B. 12,99)",
      "invalidCount": "Bitte eine ganze Zahl größer 0 eingeben"
    },
    "en": {
      "addPriceTag": "Add price tag",
      "title": "Product title",
      "price": "Price",
      "count": "Number of tags",
      "numberOptional": "Product number (optional)",
      "label": "Reusable / disposable",
      "labelNone": "None",
      "labelMehrweg": "Mehrweg",
      "labelEinweg": "Einweg",
      "bottlesPerBundleOptional": "Bottles per bundle (optional)",
      "depositPerBottle": "Deposit per bottle",
      "add": "Add",
      "cancel": "Cancel",
      "invalidAmount": "Please enter a valid amount (e.g. 12.99)",
      "invalidCount": "Please enter a whole number greater than 0"
    }
  }
</i18n>
